/**
 * Saga index: connects action type and saga
 */

import { all, takeLatest, fork, takeEvery, takeLeading } from "redux-saga/effects";

/* ------------- Types ------------- */
import {
  AuthTypes,
  ConversationTypes,
  ContactTypes,
  CallingTypes,
  DeviceTypes,
  GroupInfoTypes,
  BranchTypes,
  RestoreTypes,
  ProfileTypes,
  AccountTypes,
  SystemTypes,
  PubSubTypes,
  NotificationSettingTypes,
} from "redux-store";

/* ------------- Sagas ------------- */
import { requestLogin, requestVerify } from "./auth.saga";
import { logoutDevice } from "./account-device.saga";
import { getAccountList, blockUser, unblockUser } from "./account.saga";
import { getBranches } from "./branch.saga";
import {
  updateMessageStatus,
  markReadMessageInGroup,
  getMessageDetail,
  pinMessage,
  synchPinListRemote,
} from "./conversation-message.saga";
import {
  changeGroupPhoto,
  removeMemberGroup,
  muteGroupNotification,
  addAdmin,
  createGroup,
  updateGroup,
  deleteGroup,
  requestUploadImageCall,
  addMemberGroup,
  handleSelectConversation,
  deleteGroupOnlyMe,
} from "./conversation-info.saga";
import { findContact, updateContact, addContact, removeContact } from "./contact.saga";
import {
  checkReceiversStatus,
  checkCallingStatus,
  getCallHistory,
  updateCallHistory,
  getImageIncomingCall,
} from "./calling.saga";
import { changeProfileImage, updateProfileInfo, updateAccountStatus } from "./profile.saga";
import { synchronizeData, synchronizeAllBranchData } from "./synchronize.saga";
import { backup, changeDeviceRole, restore, verifyChangeDeviceRole, restoreToLocal } from "./restore.saga";
import { saveMsgInQueue } from "./message-queue.saga";
import { executeQueue } from "./handle-queue.saga";
import { checkBlockedContact } from "pubsub/services/conversation.service";
import { destroyBranchThread, destroyLogoutBranchThread, initPubsub, startCheckingNews } from "./pubsub.saga";
import { sendFileMessage } from "./message-file-upload.saga";
import { updateAccountSetting, updateGroupSetting, updateThreadSetting } from "./notification-setting.saga";
import { executeQueueLogout } from "./handle-queue-logout";

/* ------------- Connect Types To Sagas ------------- */
export default function* root() {
  yield fork(executeQueue);

  yield all([
    // Authentication
    takeLatest(AuthTypes.REQUEST_LOGIN, requestLogin),
    takeLatest(AuthTypes.REQUEST_VERIFY, requestVerify),

    // Account
    takeLatest(AccountTypes.GET_ACCOUNT_LIST, getAccountList),
    takeLatest(AccountTypes.BLOCK_USER, blockUser),
    takeLatest(AccountTypes.UNBLOCK_USER, unblockUser),

    // Branch
    takeLatest(BranchTypes.GET_BRANCH, getBranches),

    // Conversation Message
    takeLatest(ConversationTypes.UPDATE_MESSAGE_STATUS, updateMessageStatus),
    takeEvery(ConversationTypes.SEND_MESSAGE, saveMsgInQueue),
    takeLatest(ConversationTypes.MARK_READ_MESSAGE_IN_GROUP, markReadMessageInGroup),
    takeLatest(ConversationTypes.GET_MESSAGE_DETAIL, getMessageDetail),
    takeEvery(ConversationTypes.SEND_FILE_MESSAGE, sendFileMessage),

    // Conversation Info: Group, Channel, Personal
    takeLatest(GroupInfoTypes.CHANGE_GROUP_PHOTO, changeGroupPhoto),
    takeLatest(GroupInfoTypes.REMOVE_MEMBER_GROUP, removeMemberGroup),
    takeLatest(GroupInfoTypes.ADD_MEMBER_GROUP, addMemberGroup),
    takeLatest(GroupInfoTypes.MUTE_GROUP_NOTIFICATION, muteGroupNotification),
    takeLatest(GroupInfoTypes.ADD_ADMIN, addAdmin),
    takeLatest(GroupInfoTypes.CREATE_GROUP, createGroup),
    takeLatest(GroupInfoTypes.UPDATE_GROUP, updateGroup),
    takeLatest(GroupInfoTypes.DELETE_GROUP, deleteGroup),
    takeLatest(ConversationTypes.REQUEST_UPLOAD_IMAGE_CALL, requestUploadImageCall),
    takeLatest(ConversationTypes.SET_SELECT_GROUP_ID, handleSelectConversation),
    takeLatest(ConversationTypes.CHECK_BLOCKED_CONTACT, checkBlockedContact),

    // Contact
    takeLatest(ContactTypes.FIND_CONTACT, findContact),
    takeLatest(ContactTypes.UPDATE_CONTACT, updateContact),
    takeLatest(ContactTypes.ADD_CONTACT, addContact),
    takeLatest(ContactTypes.REMOVE_CONTACT, removeContact),

    // Calling
    takeLatest(CallingTypes.ON_CALL_CHECK, checkCallingStatus),
    takeLatest(CallingTypes.ON_CHECK_RECEIVERS_STATUS, checkReceiversStatus),
    takeLatest(CallingTypes.GET_CALL_HISTORY, getCallHistory),
    takeLatest(CallingTypes.UPDATE_CALL_HISTORY, updateCallHistory),
    takeLatest(CallingTypes.GET_IMAGE_INCOMING_CALL, getImageIncomingCall),

    // Device Management
    takeLatest(DeviceTypes.DEVICE_LOGOUT, logoutDevice),

    // Profile
    takeLatest(ProfileTypes.CHANGE_PROFILE_IMAGE, changeProfileImage),
    takeLatest(ProfileTypes.UPDATE_PROFILE_STATUS, updateAccountStatus),
    takeLatest(ProfileTypes.UPDATE_PROFILE_INFO, updateProfileInfo),

    // Synchronize
    takeLatest(SystemTypes.SYNCHRONIZE_DATA, synchronizeData),
    takeLatest(SystemTypes.SYNCHRONIZE_ALL_BRANCH_DATA, synchronizeAllBranchData),
    takeLeading(SystemTypes.EXECUTE_QUEUE, executeQueue),

    // Backup/ Restore Data
    takeLatest(RestoreTypes.CHANGE_DEVICE_ROLE, changeDeviceRole),
    takeLatest(RestoreTypes.VERIFY_CHANGE_DEVICE_ROLE, verifyChangeDeviceRole),
    takeLatest(RestoreTypes.RESTORE, restore),
    takeLatest(RestoreTypes.RESTORE_TO_LOCAL, restoreToLocal),
    takeLatest(RestoreTypes.BACKUP, backup),

    // Pubsub events
    takeLatest(PubSubTypes.INIT_PUBSUB, initPubsub),
    takeLatest(PubSubTypes.START_CHECKING_NEWS, startCheckingNews),
    takeLatest(PubSubTypes.DESTROY_BRANCH_THREAD, destroyBranchThread),
    takeLatest(PubSubTypes.DESTROY_LOGOUT_BRANCH_THREAD, destroyLogoutBranchThread),

    // Pin message
    takeLatest(ConversationTypes.PIN_MESSAGE, pinMessage),
    takeLatest(ConversationTypes.SYNCH_PIN, synchPinListRemote),

    // Notification Setting
    takeLatest(NotificationSettingTypes.UPDATE_ACCOUNT_SETTING, updateAccountSetting),
    takeLatest(NotificationSettingTypes.UPDATE_GROUP_SETTING, updateGroupSetting),
    takeLatest(NotificationSettingTypes.UPDATE_THREAD_SETTING, updateThreadSetting),

    // Delete group only me
    takeLatest(GroupInfoTypes.DELETE_GROUP_ONLY_ME, deleteGroupOnlyMe),

    takeLatest(SystemTypes.EXECUTE_QUEUE_LOGOUT, executeQueueLogout),
  ]);
}
